import axios from 'axios';
import { API_URL } from '../config';

const orgBase = (orgId) => `${API_URL}/organizations/${orgId}/agents`;
const agentBase = (orgId, agentId) => `${orgBase(orgId)}/${agentId}`;

// ---------- agents ----------

export async function fetchAgents(orgId) {
  const { data } = await axios.get(orgBase(orgId));
  return data;
}

export async function createAgent(orgId, payload) {
  const { data } = await axios.post(orgBase(orgId), payload);
  return data;
}

export async function fetchAgent(orgId, agentId) {
  const { data } = await axios.get(agentBase(orgId, agentId));
  return data;
}

export async function updateAgent(orgId, agentId, payload) {
  const { data } = await axios.patch(agentBase(orgId, agentId), payload);
  return data;
}

export async function deleteAgent(orgId, agentId) {
  const { data } = await axios.delete(agentBase(orgId, agentId));
  return data;
}

// ---------- knowledge sources ----------

export async function fetchKnowledgeSources(orgId, agentId) {
  const { data } = await axios.get(`${agentBase(orgId, agentId)}/knowledge-sources`);
  return data;
}

export async function saveKnowledgeSources(orgId, agentId, payload) {
  const { data } = await axios.put(`${agentBase(orgId, agentId)}/knowledge-sources`, payload);
  return data;
}

export async function syncKnowledgeSources(orgId, agentId, spaceId) {
  const { data } = await axios.post(
    `${agentBase(orgId, agentId)}/knowledge-sources/sync`,
    null,
    { params: spaceId ? { space_id: spaceId } : {} }
  );
  return data;
}

// ---------- config & versions ----------

export async function fetchConfig(orgId, agentId) {
  const { data } = await axios.get(`${agentBase(orgId, agentId)}/config`);
  return data;
}

export async function saveConfig(orgId, agentId, payload) {
  const { data } = await axios.put(`${agentBase(orgId, agentId)}/config`, payload);
  return data;
}

export async function publishConfig(orgId, agentId, summary) {
  const { data } = await axios.post(`${agentBase(orgId, agentId)}/config/publish`, { summary });
  return data;
}

export async function fetchConfigVersions(orgId, agentId) {
  const { data } = await axios.get(`${agentBase(orgId, agentId)}/config/versions`);
  return data;
}

export async function restoreConfigVersion(orgId, agentId, version) {
  const { data } = await axios.post(
    `${agentBase(orgId, agentId)}/config/versions/${version}/restore`
  );
  return data;
}

// ---------- voices ----------

export async function fetchVoices(orgId, agentId) {
  const { data } = await axios.get(`${agentBase(orgId, agentId)}/voices`);
  return data;
}

// ---------- channels ----------

export async function fetchChannels(orgId, agentId) {
  const { data } = await axios.get(`${agentBase(orgId, agentId)}/channels`);
  return data;
}

export async function createChannel(orgId, agentId, payload) {
  const { data } = await axios.post(`${agentBase(orgId, agentId)}/channels`, payload);
  return data;
}

export async function updateChannel(orgId, agentId, channelId, payload) {
  const { data } = await axios.patch(
    `${agentBase(orgId, agentId)}/channels/${channelId}`,
    payload
  );
  return data;
}

export async function rotateChannelKey(orgId, agentId, channelId) {
  const { data } = await axios.post(
    `${agentBase(orgId, agentId)}/channels/${channelId}/rotate-key`
  );
  return data;
}

export async function deleteChannel(orgId, agentId, channelId) {
  const { data } = await axios.delete(`${agentBase(orgId, agentId)}/channels/${channelId}`);
  return data;
}

// ---------- chat ----------

export async function sendChatMessage(orgId, agentId, payload) {
  const { data } = await axios.post(`${agentBase(orgId, agentId)}/chat`, payload);
  return data;
}

// ---------- sessions ----------

export async function fetchSessions(orgId, agentId, params = {}) {
  const { data } = await axios.get(`${agentBase(orgId, agentId)}/sessions`, { params });
  return data;
}

export async function fetchSessionStats(orgId, agentId, days = 30) {
  const { data } = await axios.get(`${agentBase(orgId, agentId)}/sessions/stats`, {
    params: { days },
  });
  return data;
}

export async function fetchSession(orgId, agentId, sessionId) {
  const { data } = await axios.get(`${agentBase(orgId, agentId)}/sessions/${sessionId}`);
  return data;
}

export async function updateSession(orgId, agentId, sessionId, payload) {
  const { data } = await axios.patch(
    `${agentBase(orgId, agentId)}/sessions/${sessionId}`,
    payload
  );
  return data;
}

export async function deleteSession(orgId, agentId, sessionId) {
  const { data } = await axios.delete(`${agentBase(orgId, agentId)}/sessions/${sessionId}`);
  return data;
}

export async function submitMessageFeedback(orgId, agentId, messageId, payload) {
  const { data } = await axios.post(
    `${agentBase(orgId, agentId)}/messages/${messageId}/feedback`,
    payload
  );
  return data;
}

// ---------- knowledge gaps ----------

export async function fetchGaps(orgId, agentId, params = {}) {
  const { data } = await axios.get(`${agentBase(orgId, agentId)}/gaps`, { params });
  return data;
}

export async function updateGap(orgId, agentId, gapId, status) {
  const { data } = await axios.patch(`${agentBase(orgId, agentId)}/gaps/${gapId}`, { status });
  return data;
}

// ---------- dashboard ----------

export async function fetchAgentDashboard(orgId, agentId, days = 7) {
  const { data } = await axios.get(`${agentBase(orgId, agentId)}/dashboard`, {
    params: { days },
  });
  return data;
}
